const API_BASE_URL = '/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = localStorage.getItem('adminToken');
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('adminToken', token);
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    // Derive tenant id from host or global header storage
    try {
      const host = window.location?.host || '';
      const parts = host.split('.');
      // Use first subdomain if present (e.g., firma1.domain.com -> firma1)
      const tenantId = (parts.length > 2) ? parts[0] : null;
      const explicitTenant = localStorage.getItem('tenantId');
      const headerTenant = explicitTenant || tenantId;
      if (headerTenant) {
        headers['X-Tenant-ID'] = headerTenant;
      }
    } catch {}

    return headers;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: this.getHeaders(),
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        if (response.status === 401) {
          // Token geçersiz, kullanıcıyı logout yap
          localStorage.removeItem('adminToken');
          window.location.href = '/login';
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Authentication
  async login(username, password) {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    
    if (response.success) {
      this.setToken(response.token);
    }
    
    return response;
  }

  logout() {
    localStorage.removeItem('adminToken');
    this.token = null;
  }

  // System Info
  async getSystemInfo() {
    return await this.request('/system/info');
  }

  // Setup & Initialization
  async getSetupStatus() {
    return await this.request('/setup/status');
  }

  async initializeSystem(setupData) {
    return await this.request('/setup/initialize', {
      method: 'POST',
      body: JSON.stringify(setupData),
    });
  }

  async deployServices(deploymentData) {
    return await this.request('/setup/deploy-services', {
      method: 'POST',
      body: JSON.stringify(deploymentData),
    });
  }

  // Tenants
  async getTenants() {
    return await this.request('/tenants');
  }

  async getTenant(id) {
    return await this.request(`/tenants/${id}`);
  }

  async createTenant(tenantData) {
    return await this.request('/tenants', {
      method: 'POST',
      body: JSON.stringify(tenantData),
    });
  }

  async updateTenant(id, tenantData) {
    return await this.request(`/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(tenantData),
    });
  }

  async deleteTenant(id) {
    return await this.request(`/tenants/${id}`, {
      method: 'DELETE',
    });
  }

  async activateTenant(id) {
    return await this.request(`/tenants/${id}/activate`, {
      method: 'PUT',
    });
  }

  async deactivateTenant(id) {
    return await this.request(`/tenants/${id}/deactivate`, {
      method: 'PUT',
    });
  }

  // Docker Container Management
  async getTenantContainers(tenantId) {
    return await this.request(`/tenants/${tenantId}/containers`);
  }

  async createTenantDockerContainers(tenantId, containerConfigs) {
    return await this.request(`/tenants/${tenantId}/containers`, {
      method: 'POST',
      body: JSON.stringify({ containers: containerConfigs }),
    });
  }

  async startTenantContainer(tenantId, containerName) {
    return await this.request(`/tenants/${tenantId}/containers/${containerName}/start`, {
      method: 'POST',
    });
  }

  async stopTenantContainer(tenantId, containerName) {
    return await this.request(`/tenants/${tenantId}/containers/${containerName}/stop`, {
      method: 'POST',
    });
  }

  async restartTenantContainer(tenantId, containerName) {
    return await this.request(`/tenants/${tenantId}/containers/${containerName}/restart`, {
      method: 'POST',
    });
  }

  async deleteTenantContainer(tenantId, containerName) {
    return await this.request(`/tenants/${tenantId}/containers/${containerName}`, {
      method: 'DELETE',
    });
  }

  async getContainerLogs(tenantId, containerName, lines = 100) {
    return await this.request(`/tenants/${tenantId}/containers/${containerName}/logs?lines=${lines}`);
  }

  async getContainerStats(tenantId, containerName) {
    return await this.request(`/tenants/${tenantId}/containers/${containerName}/stats`);
  }

  // Backup Management
  async createTenantBackup(tenantId, backupType = 'full') {
    return await this.request(`/tenants/${tenantId}/backup`, {
      method: 'POST',
      body: JSON.stringify({ type: backupType }),
    });
  }

  async getTenantBackups(tenantId) {
    return await this.request(`/tenants/${tenantId}/backup`);
  }

  async restoreTenantBackup(tenantId, backupId) {
    return await this.request(`/tenants/${tenantId}/backup/${backupId}/restore`, {
      method: 'POST',
    });
  }

  async deleteTenantBackup(tenantId, backupId) {
    return await this.request(`/tenants/${tenantId}/backup/${backupId}`, {
      method: 'DELETE',
    });
  }

  // Available Portals & Services
  async getAvailablePortals() {
    return await this.request('/available-portals');
  }

  async getAvailableServices() {
    return await this.request('/available-services');
  }

  // Users
  async getUsers() {
    return await this.request('/users');
  }

  async getUser(id) {
    return await this.request(`/users/${id}`);
  }

  async createUser(userData) {
    return await this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(id, userData) {
    return await this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(id) {
    return await this.request(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  // Master Data
  async getUnits() {
    return await this.request('/masterdata/units');
  }

  async getCountries() {
    return await this.request('/masterdata/countries');
  }

  async getCities(countryId) {
    return await this.request(`/masterdata/cities/${countryId}`);
  }

  async getLanguages() {
    return await this.request('/masterdata/languages');
  }

  async getCurrencies() {
    return await this.request('/masterdata/currencies');
  }

  async getVehicleBrands() {
    return await this.request('/masterdata/vehicle-brands');
  }

  async getVehicleModels(brandId) {
    return await this.request(`/masterdata/vehicle-models/${brandId}`);
  }

  // Integrations
  async getIntegrations() {
    return await this.request('/integrations');
  }

  // System Logs
  async getSystemLogs(limit = 100) {
    return await this.request(`/system/logs?limit=${limit}`);
  }

  // System Metrics
  async getSystemMetrics(metricName = null, limit = 50) {
    const params = new URLSearchParams();
    if (metricName) params.append('metric_name', metricName);
    params.append('limit', limit);
    
    return await this.request(`/system/metrics?${params.toString()}`);
  }

  // System Settings
  async getSystemSettings() {
    return await this.request('/system/settings');
  }

  async updateSystemSetting(key, value) {
    return await this.request(`/system/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  }

  // Orchestration API
  async getOrchestrationContainers(tenantId) {
    return await this.request(`/orchestration/tenants/${tenantId}/containers`);
  }

  async createOrchestrationContainers(tenantId, containerConfigs) {
    return await this.request(`/orchestration/tenants/${tenantId}/containers`, {
      method: 'POST',
      body: JSON.stringify(containerConfigs),
    });
  }

  async startOrchestrationContainer(tenantId, containerName) {
    return await this.request(`/orchestration/tenants/${tenantId}/containers/${containerName}/start`, {
      method: 'POST',
    });
  }

  async stopOrchestrationContainer(tenantId, containerName) {
    return await this.request(`/orchestration/tenants/${tenantId}/containers/${containerName}/stop`, {
      method: 'POST',
    });
  }

  async restartOrchestrationContainer(tenantId, containerName) {
    return await this.request(`/orchestration/tenants/${tenantId}/containers/${containerName}/restart`, {
      method: 'POST',
    });
  }

  async deleteOrchestrationContainer(tenantId, containerName) {
    return await this.request(`/orchestration/tenants/${tenantId}/containers/${containerName}`, {
      method: 'DELETE',
    });
  }

  async getOrchestrationContainerLogs(tenantId, containerName, lines = 100) {
    return await this.request(`/orchestration/tenants/${tenantId}/containers/${containerName}/logs?lines=${lines}`);
  }

  async getOrchestrationContainerStats(tenantId, containerName) {
    return await this.request(`/orchestration/tenants/${tenantId}/containers/${containerName}/stats`);
  }

  async updateOrchestrationContainer(tenantId, containerName, config) {
    return await this.request(`/orchestration/tenants/${tenantId}/containers/${containerName}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  // Nginx Management
  async reloadNginx() {
    return await this.request('/orchestration/nginx/reload', {
      method: 'POST',
    });
  }

  async getNginxStatus() {
    return await this.request('/orchestration/nginx/status');
  }
}

const apiService = new ApiService();
export default apiService;
